import { inject, Injector } from '@angular/core';
import { ApolloClient, ApolloLink, InMemoryCache } from '@apollo/client/core';
import { CombinedGraphQLErrors } from '@apollo/client/errors';
import { SetContextLink } from '@apollo/client/link/context';
import { ErrorLink } from '@apollo/client/link/error';
import { TranslocoService } from '@jsverse/transloco';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore — apollo-upload-client@20 ships ESM modules without bundled types
import UploadHttpLink from 'apollo-upload-client/UploadHttpLink.mjs';
import { environment } from 'environments/environment';
import { EMPTY, from, switchMap } from 'rxjs';
import { isTokenExpired } from '../authentication/shared/authentication';
import { SessionRecoveryCoordinatorService } from '../authentication/shared/session-recovery-coordinator.service';
import { TokenStore } from '../authentication/shared/token-store.service';
import { extractGraphqlErrorTranslations } from './graphql';
import type { GraphQLErrorHandler, GraphQLOriginalError } from './graphql.types';
import { GRAPHQL_ERROR_HANDLER } from './handlers/graphql.tokens';

// ---------------------------------------------------------------------------
// createAuroraGraphqlLinks — pure factory, injectable or testable
// ---------------------------------------------------------------------------

/**
 * Operation-context key stamped by `headersLink` with the `TokenStore.identityGeneration()` the
 * request was actually SENT under (design.md Decision 3 — replaces the old boolean
 * `IMPERSONATED_REQUEST_CONTEXT_KEY`).
 *
 * A boolean marker could only say THAT an operation was impersonated, not WHICH session: it could
 * not distinguish one impersonation from a later one, so a late failure from an ended
 * impersonation could unwind a different, still-valid one. The generation is a monotonic counter
 * over the `(account, actor)` identity tuple, so the coordinator can compare "the identity this
 * operation was issued under" against "the identity active now" — both when the failure is
 * handled AND immediately before `forward(operation)`, closing the re-send window a network round
 * trip (the refresh grant) opens.
 */
export const IDENTITY_GENERATION_CONTEXT_KEY = '__identityGeneration';

/**
 * Dependencies accepted by createAuroraGraphqlLinks.
 * Splitting them out allows unit tests to pass a terminating mock link
 * instead of a real UploadHttpLink that would require a network and a URI.
 */
export interface AuroraGraphqlLinksDeps {
  translocoService: TranslocoService;
  errorHandler: GraphQLErrorHandler;
  tokenStore: Pick<
    TokenStore,
    'accessToken' | 'refreshToken' | 'isImpersonating' | 'identityGeneration'
  >;
  /**
   * Captured DI `Injector`, used to resolve `SessionRecoveryCoordinatorService` LAZILY — only
   * inside the error-link callback, when a recovery is actually needed — so the Apollo client
   * never acquires a construction-time dependency on it (design.md constraint 2).
   */
  injector: Injector;
  /** The terminating link. Production code passes a real UploadHttpLink. */
  httpLink: ApolloLink;
}

/**
 * Builds and returns the composed Apollo link chain.
 *
 * Chain order: [errorLink, headersLink, httpLink]
 *
 * IMPORTANT — why errorLink comes FIRST:
 * In Apollo, `forward(operation)` inside a link only re-executes links
 * DOWNSTREAM of it (i.e., links that appear AFTER it in the array).
 * `headersLink` (SetContextLink) reads `tokenStore.accessToken()` and attaches
 * the Bearer token.  If headersLink were placed BEFORE errorLink, the retry
 * `forward(operation)` call inside errorLink would skip headersLink and send
 * the request without the fresh token, causing an immediate second 401 and
 * routing to a terminal sign-out.
 *
 * By placing errorLink first, `forward(operation)` on retry re-runs headersLink,
 * which reads the now-fresh token written by the coordinator's refresh grant, and the
 * retried request succeeds.
 */
export const createAuroraGraphqlLinks = ({
  translocoService,
  errorHandler,
  tokenStore,
  injector,
  httpLink,
}: AuroraGraphqlLinksDeps): ApolloLink => {
  const headersLink = new SetContextLink((prevContext) => {
    const headers: Record<string, string> = {};

    // merge any per-operation context headers.
    const previousHeaders = (prevContext as { headers?: unknown }).headers;
    if (previousHeaders && typeof previousHeaders === 'object') {
      Object.assign(headers, previousHeaders as Record<string, string>);
    }

    // timezone
    headers['X-Timezone'] = Intl.DateTimeFormat().resolvedOptions().timeZone;

    const token = tokenStore.accessToken();
    if (token && !isTokenExpired(token)) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    // Stamped on EVERY user/Bearer operation — including one whose token was already locally
    // expired (no header attached) — so the coordinator can always tell which identity the
    // request was issued under, even after the store has since moved on.
    return { headers, [IDENTITY_GENERATION_CONTEXT_KEY]: tokenStore.identityGeneration() };
  });

  const errorLink = new ErrorLink(({ error, operation, forward }) => {
    if (CombinedGraphQLErrors.is(error)) {
      // Classify the auth failures we care about by their backend originalError.
      const hasOriginal = (statusCode: number, errorCode: string): boolean =>
        error.errors.some((graphqlError) => {
          const ext = graphqlError.extensions as Record<string, unknown> | undefined;
          const original = ext?.['originalError'] as GraphQLOriginalError | undefined;
          if (!original) return false;

          return original.statusCode === statusCode && original.error === errorCode;
        });

      // Bearer no longer accepted (expired, revoked, row gone): RFC 6750 `invalid_token`.
      // Recoverable via refresh; never matched on `message`, which is free text.
      const isAccessExpiry = hasOriginal(401, 'invalid_token');

      // No `isRefreshInvalid` classification here (deliberately removed): the genuine refresh-grant
      // failure is `InvalidGrantError`, an HTTP 400 `{statusCode: 400, error: 'invalid_grant'}`,
      // already handled by SessionRecoveryCoordinatorService. A 404 `OAuthRefreshToken ...` is NEVER
      // produced by an authentication path — the only three producers are the o-auth/refresh-token
      // admin CRUD handlers (find / find-by-id / delete-by-id) reporting a missing row. That is an
      // admin not-found, not an unrecoverable auth failure, so it falls through to the generic toast
      // path below, exactly like its sibling `OAuthAccessToken` admin CRUD 404 already does.

      if (isAccessExpiry) {
        const context = operation.getContext() as Record<string, unknown>;

        // Retry-once guard (D5): an already-retried op that 401s again must terminate directly,
        // never loop back into a fresh refresh attempt.
        const alreadyRetried = context['__refreshRetried'] === true;

        // CHAIN-CONDITIONAL, NOT DEAD CODE — do not delete this branch as unreachable. Three
        // MEASURED facts, recorded here so nobody has to re-measure them:
        //
        //  1. On the RETRY path it never fires: the error link runs exactly ONCE per operation,
        //     even though the transport is hit twice.
        //  2. The reason is `ErrorLink`'s own implementation, not `forward()` semantics in
        //     general. It opens a SINGLE subscription downstream and invokes this callback only
        //     from that subscription's handlers; an Observable the callback returns is piped
        //     straight to the outer observer (`retriedResult.subscribe(observer)`) with no error
        //     inspection wrapped around it — so a retry's own failure structurally cannot
        //     re-enter here.
        //  3. It DOES fire when the transport emits MORE THAN ONE errored payload for a single
        //     operation (`@defer` / `@stream` / multipart, subscriptions): the callback then runs
        //     once per emission and the later run sees the flag already set. Aurora emits one
        //     payload per operation today, so the branch is dormant — adding any of those makes
        //     it load-bearing overnight.
        //
        // Its regression test is `apollo.factory.spec.ts` › "terminates instead of refreshing
        // again when a second errored payload arrives for the same operation".
        //
        // An already-retried op DOES carry a user identity — pass the generation it was stamped
        // with so a late failure from an identity that no longer exists cannot terminate whatever
        // DIFFERENT, currently valid session is active now.
        if (alreadyRetried) {
          const coordinator = injector.get(SessionRecoveryCoordinatorService);
          return from(
            coordinator.terminate(context[IDENTITY_GENERATION_CONTEXT_KEY] as number),
          ).pipe(switchMap(() => EMPTY));
        }

        // Mark before retrying so a LATER errored payload for this SAME operation falls through
        // to termination (see the guard above) instead of starting a second refresh grant. The
        // retried request's own 401 never arrives here — fact 2 above.
        operation.setContext({ __refreshRetried: true });

        const generation = context[IDENTITY_GENERATION_CONTEXT_KEY] as number;
        // Resolved LAZILY — only reached when a recovery is actually needed — so the Apollo
        // client never acquires a construction-time dependency on the coordinator.
        const coordinator = injector.get(SessionRecoveryCoordinatorService);

        return coordinator
          .recoverForOperation(generation)
          .pipe(switchMap((outcome) => (outcome === 'retry' ? forward(operation) : EMPTY)));
      }

      // Per-operation opt-in: callers may set context.skipGlobalErrors to suppress specific
      // RFC 6749 error codes from the global toast (e.g. 'invalid_grant' for inline display).
      // skipCodes is passed directly into extractGraphqlErrorTranslations so errors are filtered
      // BEFORE translation — no index-drift possible in multi-error responses.
      const ctx = operation.getContext() as Record<string, unknown>;
      const skipCodes = Array.isArray(ctx['skipGlobalErrors'])
        ? (ctx['skipGlobalErrors'] as string[])
        : [];

      const toastErrors = extractGraphqlErrorTranslations(
        error.errors,
        (key, params) => translocoService.translate(key, params),
        skipCodes,
      );

      if (toastErrors.length > 0) {
        errorHandler.handleGraphQLErrors(toastErrors);
      }
    } else {
      // network or other error
      const statusCode =
        'status' in error ? ((error as Record<string, unknown>)['status'] as number) : undefined;
      errorHandler.handleNetworkError(error, statusCode);
    }

    // No retry: every non-refresh path returns void (the callback may return an
    // Observable only to retry the operation).
    return;
  });

  // errorLink MUST come before headersLink so that forward(operation) on retry
  // re-runs headersLink and re-attaches the fresh Bearer token. See JSDoc above.
  return ApolloLink.from([errorLink, headersLink, httpLink]);
};

// ---------------------------------------------------------------------------
// apolloFactory — Angular DI entry point
// ---------------------------------------------------------------------------

export const apolloFactory = (): ApolloClient.Options => {
  const translocoService = inject(TranslocoService);
  const errorHandler = inject(GRAPHQL_ERROR_HANDLER);
  const tokenStore = inject(TokenStore);
  // Captured, never dereferenced here — only inside the error-link callback (design.md
  // constraint 2). Injecting `Injector` itself creates no construction-time dependency on
  // `SessionRecoveryCoordinatorService`.
  const injector = inject(Injector);

  const http = new UploadHttpLink({ uri: environment.api.graphql }) as ApolloLink;

  const link = createAuroraGraphqlLinks({
    translocoService,
    errorHandler,
    tokenStore,
    injector,
    httpLink: http,
  });

  const cache = new InMemoryCache();

  const defaultOptions: ApolloClient.DefaultOptions.Input = {
    watchQuery: {
      fetchPolicy: 'no-cache',
      errorPolicy: 'ignore',
    },
    query: {
      fetchPolicy: 'no-cache',
      errorPolicy: 'all',
    },
  };

  return { link, cache, defaultOptions };
};
